// Per-page SEO component using react-helmet-async.
// Handles: title, description, canonical, OpenGraph, Twitter Card,
// hreflang alternates for DE/EN/FR, and optional JSON-LD structured data.

import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";
import { useI18n } from "@/lib/i18nContext";
import { stripLangPrefix } from "@/lib/i18n";

const SITE_ORIGIN = "https://karimbenna-itconsult.de";
const DEFAULT_OG_IMAGE = "/og/og-default.png";
const SITE_NAME = "Karim Benna IT Consulting";

const LANG_TO_OG = { de: "de_DE", en: "en_GB", fr: "fr_FR" };
const SUPPORTED = ["de", "en", "fr"];

function buildUrl(path) {
  const clean = path.startsWith("/") ? path : `/${path}`;
  return SITE_ORIGIN + clean;
}

/**
 * <Seo /> – place at the top of each page component.
 *
 * Props:
 *  - title:        page-specific title (will be appended with site name)
 *  - description:  short meta description (max ~155 chars)
 *  - image:        absolute path or full URL for OG/Twitter image (optional)
 *  - type:         OG type ("website" | "article" | "profile")
 *  - article:      optional { publishedAt, modifiedAt, author, tags[] } for blog posts
 *  - jsonLd:       additional schema.org object(s) to inject as JSON-LD
 *  - noindex:      set true for admin / private pages
 */
export default function Seo({
  title,
  description,
  image,
  type = "website",
  article,
  jsonLd,
  noindex = false,
}) {
  const { lang } = useI18n();
  const { pathname } = useLocation();

  const cleanPath = stripLangPrefix(pathname) || "/";
  const fullTitle = title ? `${title} | ${SITE_NAME}` : SITE_NAME;
  const ogImage = image
    ? image.startsWith("http")
      ? image
      : SITE_ORIGIN + image
    : SITE_ORIGIN + DEFAULT_OG_IMAGE;

  // Canonical URL respects current language prefix
  const canonical = buildUrl(lang === "de" ? cleanPath : `/${lang}${cleanPath}`);

  // hreflang alternates
  const alternates = SUPPORTED.map((l) => ({
    lang: l,
    url: buildUrl(l === "de" ? cleanPath : `/${l}${cleanPath}`),
  }));

  return (
    <Helmet prioritizeSeoTags>
      {/* Document lang */}
      <html lang={lang} />

      {/* Primary meta */}
      <title>{fullTitle}</title>
      {description && <meta name="description" content={description} />}
      <link rel="canonical" href={canonical} />
      {noindex && <meta name="robots" content="noindex,nofollow" />}

      {/* hreflang alternates */}
      {alternates.map((alt) => (
        <link key={alt.lang} rel="alternate" hrefLang={alt.lang} href={alt.url} />
      ))}
      <link rel="alternate" hrefLang="x-default" href={buildUrl(cleanPath)} />

      {/* OpenGraph */}
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:type" content={type} />
      <meta property="og:locale" content={LANG_TO_OG[lang] || "de_DE"} />
      <meta property="og:title" content={fullTitle} />
      {description && <meta property="og:description" content={description} />}
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />

      {/* Article-specific OG */}
      {type === "article" && article?.publishedAt && (
        <meta property="article:published_time" content={article.publishedAt} />
      )}
      {type === "article" && article?.modifiedAt && (
        <meta property="article:modified_time" content={article.modifiedAt} />
      )}
      {type === "article" && article?.author && (
        <meta property="article:author" content={article.author} />
      )}
      {type === "article" &&
        article?.tags?.map((tag) => (
          <meta key={tag} property="article:tag" content={tag} />
        ))}

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      {description && <meta name="twitter:description" content={description} />}
      <meta name="twitter:image" content={ogImage} />

      {/* JSON-LD structured data */}
      {jsonLd && (
        <script type="application/ld+json">
          {JSON.stringify(Array.isArray(jsonLd) ? jsonLd : [jsonLd])}
        </script>
      )}
    </Helmet>
  );
}

// ─── Shared JSON-LD builders (use these in pages) ──────────────────────────

export const personSchema = {
  "@context": "https://schema.org",
  "@type": "Person",
  name: "Karim Benna",
  url: SITE_ORIGIN,
  image: SITE_ORIGIN + "/karim-portrait.jpg",
  jobTitle: "IT Consultant · Software Engineer · Product Owner",
  description:
    "Freelance IT consultant for backend development, quality assurance, AI consulting and technical project coordination — based in Germany & Portugal.",
  email: "mailto:contact@karimbenna-itconsult.com",
  knowsLanguage: ["de", "en", "fr", "ar"],
  alumniOf: [
    {
      "@type": "CollegeOrUniversity",
      name: "Ludwig Maximilian University of Munich",
      sameAs: "https://www.lmu.de",
    },
    {
      "@type": "EducationalOrganization",
      name: "Massachusetts Institute of Technology (MIT)",
      sameAs: "https://www.mit.edu",
    },
  ],
  knowsAbout: [
    "Java",
    "Spring Boot",
    "Microservices",
    "TypeScript",
    "GraphQL",
    "React",
    "React Native",
    "QA",
    "Test Management",
    "Product Ownership",
    "Generative AI",
    "Applied AI",
    "Data Science",
  ],
  worksFor: {
    "@type": "Organization",
    name: SITE_NAME,
    url: SITE_ORIGIN,
  },
};

export const websiteSchema = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  name: SITE_NAME,
  url: SITE_ORIGIN,
  inLanguage: ["de", "en", "fr"],
  publisher: {
    "@type": "Person",
    name: "Karim Benna",
  },
  potentialAction: {
    "@type": "SearchAction",
    target: `${SITE_ORIGIN}/blog?q={search_term_string}`,
    "query-input": "required name=search_term_string",
  },
};

export function blogPostingSchema({
  title,
  description,
  slug,
  publishedAt,
  modifiedAt,
  image,
  lang,
  tags,
}) {
  const url = buildUrl(lang === "de" ? `/blog/${slug}` : `/${lang}/blog/${slug}`);
  return {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: title,
    description,
    inLanguage: lang,
    datePublished: publishedAt,
    dateModified: modifiedAt || publishedAt,
    author: {
      "@type": "Person",
      name: "Karim Benna",
      url: SITE_ORIGIN,
    },
    publisher: {
      "@type": "Person",
      name: "Karim Benna",
      url: SITE_ORIGIN,
    },
    image: image ? (image.startsWith("http") ? image : SITE_ORIGIN + image) : undefined,
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": url,
    },
    url,
    keywords: tags?.join(", "),
  };
}

export function breadcrumbSchema(items) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: item.name,
      item: item.url.startsWith("http") ? item.url : SITE_ORIGIN + item.url,
    })),
  };
}

export { SITE_ORIGIN, SITE_NAME };
